import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { getBadgeEmoji, getBadgeAccent, getBadgeRing, hasCustomIcon } from './badgeConfig'
import { decodeHtmlEntities } from '../../utils/displayText'

/** Custom "just starting" icon for Agenda Starter: checkered start flag (race start = beginning). */
function AgendaStarterIcon({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 8v48l6-10 6 10 6-10 6 10 6-10 6 10V8H14z" fill="#F59E0B" stroke="#D97706" strokeWidth="2" strokeLinejoin="round" />
      <path d="M20 14h20M20 24h16M20 34h20M20 44h14" stroke="#FEF3C7" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Big, bold, pictorial badge card: large emoji (or custom icon) on top, then name, then description.
 * Each badge has a unique emoji that correlates to its meaning.
 */
export default function BadgeCard({ badge, className = '', isNewAward = false, onAwardAnimated, awardDelay = 0, compact = false }) {
  const { badgeKey, name, description, category, count } = useMemo(() => {
    const def = badge?.definition
    return {
      badgeKey: def?.badgeKey ?? badge?.badgeKey ?? '',
      name: def?.name ?? badge?.name ?? 'Badge',
      description: def?.description ?? badge?.description ?? null,
      category: def?.category ?? badge?.category ?? 'agenda',
      count: badge?.count ?? 1,
    }
  }, [badge])

  const emoji = getBadgeEmoji(badgeKey)
  const custom = hasCustomIcon(badgeKey)
  const accent = getBadgeAccent(category)
  const ring = getBadgeRing(category)

  const tooltipText = count > 1
    ? `Earned ${count} times. ${decodeHtmlEntities(description || name)}`
    : decodeHtmlEntities(description || name)

  const cardContent = (
    <div
      data-badge-key={badgeKey}
      title={tooltipText}
      data-tooltip={tooltipText}
      className={`
        tooltip tooltip-wrap
        relative flex flex-col items-center justify-center rounded-xl
        ${compact
          ? 'px-1.5 py-1.5 w-[72px] h-[64px]'
          : 'px-2.5 py-2 sm:px-3 sm:py-2.5 w-[108px] h-[96px]'
        }
        shrink-0
        bg-white/90 bg-gradient-to-b ${accent} ${ring}
        transition-shadow text-center cursor-default
        ${className}
      `}
    >
      {/* Compact count in top-right when multiple */}
      {count > 1 && (
        <span
          className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-0.5 rounded bg-gray-900/85 text-white text-[9px] font-bold flex items-center justify-center"
          aria-label={`Earned ${count} times`}
        >
          {count}×
        </span>
      )}
      {/* Emoji or custom icon on top */}
      <div className={`flex items-center justify-center ${compact ? 'mb-0.5 min-h-[20px]' : 'mb-1 min-h-[28px]'}`}>
        {custom && badgeKey === 'agenda_starter' ? (
          <AgendaStarterIcon className={compact ? 'w-5 h-5 drop-shadow-sm' : 'w-8 h-8 sm:w-9 sm:h-9 drop-shadow-sm'} />
        ) : (
          <span
            className={`${compact ? 'text-lg' : 'text-2xl sm:text-3xl'} drop-shadow-sm leading-none`}
            role="img"
            aria-hidden
          >
            {emoji ?? '✨'}
          </span>
        )}
      </div>
      {/* Bold name */}
      <h3 className={`font-bold text-gray-900 leading-tight px-0.5 break-words ${compact ? 'text-[9px]' : 'text-[11px] sm:text-xs'}`}>
        {decodeHtmlEntities(name)}
      </h3>
    </div>
  )

  if (isNewAward) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -100, scale: 0.3, rotate: -8 }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          rotate: 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 80,
          damping: 18,
          mass: 1.2,
          delay: awardDelay,
        }}
        onAnimationComplete={onAwardAnimated}
      >
        {cardContent}
      </motion.div>
    )
  }

  return cardContent
}
